import { c as _c } from "react/compiler-runtime";
import { subHours } from 'date-fns';
import { useContext } from 'react';
import NumberFormat from 'react-number-format';
import { useHistory } from 'react-router';
import { NavLink } from 'react-router-dom';
import styled, { css, useTheme } from 'styled-components';
import useSWR from 'swr';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import LINKS from '../../../../configs/links';
import { capitalizeFirstLetter } from '../../../../utils';
import { showChat } from '../../../../utils/liveChat';
import { storage_keys } from '../../../../utils/storage';
import { isFreePlan } from '../../../../utils/subscription';
import Avatar from '../../../common/base/Avatar';
import Badge from '../../../common/base/Badge';
import Button, { ClickableArea } from '../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../common/base/Card';
import Divider from '../../../common/base/Divider';
import Icon from '../../../common/base/Icon';
import ProgressBar from '../../../common/base/ProgressBar';
import Text from '../../../common/base/Text';
import Tooltip from '../../../common/base/Tooltip';
import { formatChartRateNumber } from '../../../common/Chart/format';
import LineChart from '../../../common/Chart/LineChart';
import Dropdown from '../../../common/Dropdown';
import { Div } from '../../../common/helpers/StyledUtils';
import { GlobalContext } from '../../../contexts/GlobalContext';
import useLocalStorage from '../../../hooks/useLocalStorage';
import { useMemberTrial } from '../../../hooks/useMemberTrial';
import { DashboardContext } from '../DashboardContext';
import { useRefreshKey } from '../Metrics/RefreshKeyContext';
import { ChartData, useMetric } from '../Metrics/useMetric';
import { StyledNavLinks } from './';
import CollapsedNavIcon from './CollapsedNavIcon';
import useChangelog from './Changelog/useChangelog';
import type HookdeckAPI from '../../../../client/Hookdeck';
export const handleLogout = (HookdeckAPI: HookdeckAPI) => {
  return HookdeckAPI.session.logout().then(response => {
    window.localStorage.clear();
    window.sessionStorage.clear();
    if (response?.redirect_url) {
      window.location.replace(response?.redirect_url);
    } else {
      window.location.replace('/signin');
    }
  });
};
const StyledNavFooter = styled(Div)<{
  collapsed: boolean;
}>(({
  theme,
  collapsed
}) => css`
    padding: ${theme.spacing(3)} ${theme.spacing(2)};

    border-top: 1px solid ${theme.colors.outline.neutral};
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: ${theme.colors.surface.base.background};
    z-index: ${theme.zindex.fixed};

    ${collapsed && css`
      ${StyledNavLinks} > a,
      ${StyledNavLinks} > button {
        box-sizing: border-box;
        width: ${theme.pxToRem(28)} !important;
      }
    `}
  `);
const CardsWrapper = styled(Div)`
  position: relative;
  margin: ${({
  theme
}) => theme.spacing(1)};
  margin-bottom: ${({
  theme
}) => theme.spacing(2)};

  ${StyledCard} {
    border: none;
    box-shadow: ${({
  theme
}) => theme.shadows.neutral.base};
  }
`;
const CardBackgroundHint = styled(StyledCard)`
  position: absolute;
  top: -4px;
  padding: ${({
  theme
}) => theme.spacing(4)};
  opacity: 0.8;
  transform: scaleX(0.9);
  z-index: -1;
`;
const StyledMemberTrialCard = styled(StyledCard)`
  position: relative;
  width: auto;
  z-index: 1;
`;
const MemberTrialCard = t0 => {
  const $ = _c(53);
  const {
    dismissCard
  } = t0;
  const {
    subscription,
    organization,
    organization_role
  } = useContext(DashboardContext);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    is_member_trial_active,
    is_member_trial_expired,
    member_trial_left_days
  } = useMemberTrial(subscription);
  let t1;
  if ($[0] !== organization.id) {
    t1 = APIMethodKeys.organizations.listMembers(organization.id);
    $[0] = organization.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== HookdeckAPI.organizations) {
    t2 = () => HookdeckAPI.organizations.listMembers();
    $[2] = HookdeckAPI.organizations;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: members
  } = useSWR(t1, t2);
  const exceeds_users = subscription.max_users && members && members.length >= subscription.max_users;
  const invite_to_trial = !!exceeds_users && !organization?.sso_enabled;
  if (is_member_trial_expired && ["admin", "owner"].includes(organization_role)) {
    let t3;
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        align: "center",
        justify: "space-between"
      };
      t4 = <Text semi={true}>Member trial expired</Text>;
      $[4] = t3;
      $[5] = t4;
    } else {
      t3 = $[4];
      t4 = $[5];
    }
    let t5;
    if ($[6] !== dismissCard) {
      t5 = <Div flex={t3}>{t4}<Button ghost={true} small={true} icon="close" onClick={dismissCard} /></Div>;
      $[6] = dismissCard;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    let t6;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Text muted={true}>Upgrade to restore access to members added during the trial period.</Text>;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Button to={_temp} primary={true} block={true} outline={true} m={{
        t: 3
      }}>Upgrade plan</Button>;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    let t8;
    if ($[10] !== t5) {
      t8 = <CardsWrapper><StyledMemberTrialCard><StyledCardSection p={3}>{t5}{t6}{t7}</StyledCardSection></StyledMemberTrialCard></CardsWrapper>;
      $[10] = t5;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    return t8;
  } else {
    if (is_member_trial_active && ["admin", "owner"].includes(organization_role)) {
      let t3;
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = {
          align: "center",
          justify: "space-between"
        };
        $[12] = t3;
      } else {
        t3 = $[12];
      }
      let t4;
      let t5;
      if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = {
          align: "center"
        };
        t5 = <Text semi={true}>Free member trial</Text>;
        $[13] = t4;
        $[14] = t5;
      } else {
        t4 = $[13];
        t5 = $[14];
      }
      let t6;
      if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <Div flex={t4}>{t5}<Tooltip tooltip="Invite unlimited members to this organization for a 14-day free trial period. Upgrade your plan to keep members on this organization indefinitely."><Icon icon="info" muted={true} m={{
              l: 1
            }} /></Tooltip></Div>;
        $[15] = t6;
      } else {
        t6 = $[15];
      }
      let t7;
      if ($[16] !== dismissCard) {
        t7 = <Div flex={t3}>{t6}<Button ghost={true} small={true} icon="close" onClick={dismissCard} /></Div>;
        $[16] = dismissCard;
        $[17] = t7;
      } else {
        t7 = $[17];
      }
      let t8;
      if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = {
          y: 2
        };
        $[18] = t8;
      } else {
        t8 = $[18];
      }
      const t9 = 14 - (member_trial_left_days as number) / 14;
      let t10;
      if ($[19] !== t9) {
        t10 = <ProgressBar m={t8} value={t9} />;
        $[19] = t9;
        $[20] = t10;
      } else {
        t10 = $[20];
      }
      const t11 = 14 - (member_trial_left_days as number);
      let t12;
      if ($[21] !== t11) {
        t12 = <Text muted={true}>{t11}/14 days used</Text>;
        $[21] = t11;
        $[22] = t12;
      } else {
        t12 = $[22];
      }
      let t13;
      if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = <Button to={_temp2} block={true} outline={true} data-action="invite-to-trial-navbar-card" m={{
          t: 3
        }}>Invite</Button>;
        $[23] = t13;
      } else {
        t13 = $[23];
      }
      let t14;
      if ($[24] !== t10 || $[25] !== t12 || $[26] !== t7) {
        t14 = <CardsWrapper><StyledMemberTrialCard><StyledCardSection p={3}>{t7}{t10}{t12}{t13}</StyledCardSection></StyledMemberTrialCard></CardsWrapper>;
        $[24] = t10;
        $[25] = t12;
        $[26] = t7;
        $[27] = t14;
      } else {
        t14 = $[27];
      }
      return t14;
    } else {
      if (is_member_trial_active && !["admin", "owner"].includes(organization_role)) {
        let t3;
        if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = {
            align: "center",
            justify: "space-between"
          };
          $[28] = t3;
        } else {
          t3 = $[28];
        }
        let t4;
        let t5;
        if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = {
            align: "center"
          };
          t5 = <Text semi={true}>Free trial access</Text>;
          $[29] = t4;
          $[30] = t5;
        } else {
          t4 = $[29];
          t5 = $[30];
        }
        let t6;
        if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <Div flex={t4}>{t5}<Tooltip tooltip="You can only access this organization for a limited time. In order to maintain your access beyond the trial period, the organization owner must upgrade your plan."><Icon icon="info" muted={true} m={{
                l: 1
              }} /></Tooltip></Div>;
          $[31] = t6;
        } else {
          t6 = $[31];
        }
        let t7;
        if ($[32] !== dismissCard) {
          t7 = <Div flex={t3}>{t6}<Button ghost={true} small={true} icon="close" onClick={dismissCard} /></Div>;
          $[32] = dismissCard;
          $[33] = t7;
        } else {
          t7 = $[33];
        }
        let t8;
        if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = {
            y: 2
          };
          $[34] = t8;
        } else {
          t8 = $[34];
        }
        const t9 = 14 - (member_trial_left_days as number) / 14;
        let t10;
        if ($[35] !== t9) {
          t10 = <ProgressBar m={t8} value={t9} />;
          $[35] = t9;
          $[36] = t10;
        } else {
          t10 = $[36];
        }
        const t11 = 14 - (member_trial_left_days as number);
        let t12;
        if ($[37] !== t11) {
          t12 = <Text muted={true}>{t11}/14 days used</Text>;
          $[37] = t11;
          $[38] = t12;
        } else {
          t12 = $[38];
        }
        let t13;
        if ($[39] !== t10 || $[40] !== t12 || $[41] !== t7) {
          t13 = <CardsWrapper><StyledMemberTrialCard><StyledCardSection p={3}>{t7}{t10}{t12}</StyledCardSection></StyledMemberTrialCard></CardsWrapper>;
          $[39] = t10;
          $[40] = t12;
          $[41] = t7;
          $[42] = t13;
        } else {
          t13 = $[42];
        }
        return t13;
      } else {
        if (invite_to_trial && ["admin", "owner"].includes(organization_role)) {
          let t3;
          if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
            t3 = {
              align: "center",
              justify: "space-between"
            };
            $[43] = t3;
          } else {
            t3 = $[43];
          }
          let t4;
          let t5;
          if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
            t4 = {
              align: "center"
            };
            t5 = <Text semi={true}>Free member trial</Text>;
            $[44] = t4;
            $[45] = t5;
          } else {
            t4 = $[44];
            t5 = $[45];
          }
          let t6;
          if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
            t6 = <Div flex={t4}>{t5}<Tooltip tooltip="Invite unlimited members to this organization for a 14-day free trial period. Upgrade your plan to keep members on this organization indefinitely."><Icon icon="info" muted={true} m={{
                  l: 1
                }} /></Tooltip></Div>;
            $[46] = t6;
          } else {
            t6 = $[46];
          }
          let t7;
          if ($[47] !== dismissCard) {
            t7 = <Div flex={t3}>{t6}<Button ghost={true} small={true} icon="close" onClick={dismissCard} /></Div>;
            $[47] = dismissCard;
            $[48] = t7;
          } else {
            t7 = $[48];
          }
          let t8;
          if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
            t8 = <Text muted={true}>Unlock 14-day access.</Text>;
            $[49] = t8;
          } else {
            t8 = $[49];
          }
          let t9;
          if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
            t9 = <Button to={_temp3} block={true} outline={true} m={{
              t: 3
            }} data-action="invite-to-trial-navbar-card">Invite</Button>;
            $[50] = t9;
          } else {
            t9 = $[50];
          }
          let t10;
          if ($[51] !== t7) {
            t10 = <CardsWrapper><StyledMemberTrialCard><StyledCardSection p={3}>{t7}{t8}{t9}</StyledCardSection></StyledMemberTrialCard></CardsWrapper>;
            $[51] = t7;
            $[52] = t10;
          } else {
            t10 = $[52];
          }
          return t10;
        }
      }
    }
  }
  return null;
};
const useOrganizationUsage = () => {
  const $ = _c(27);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    subscription
  } = useContext(DashboardContext);
  let t0;
  if ($[0] !== subscription) {
    t0 = !!subscription?.plan && isFreePlan(subscription.plan);
    $[0] = subscription;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const is_free_plan = t0;
  let t1;
  if ($[2] !== is_free_plan) {
    t1 = is_free_plan && APIMethodKeys.billing.getSubscriptionDetails();
    $[2] = is_free_plan;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== HookdeckAPI.billing) {
    t2 = () => HookdeckAPI.billing.getSubscriptionDetails();
    $[4] = HookdeckAPI.billing;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    data: subscription_details
  } = useSWR(t1, t2);
  let t3;
  if ($[6] !== is_free_plan || $[7] !== subscription_details?.current_billing_period_end_date || $[8] !== subscription_details?.current_billing_period_start_date) {
    t3 = is_free_plan && APIMethodKeys.billing.getUsage({
      timeframe_start: subscription_details?.current_billing_period_start_date,
      timeframe_end: subscription_details?.current_billing_period_end_date
    });
    $[6] = is_free_plan;
    $[7] = subscription_details?.current_billing_period_end_date;
    $[8] = subscription_details?.current_billing_period_start_date;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== HookdeckAPI.billing || $[11] !== subscription_details?.current_billing_period_end_date || $[12] !== subscription_details?.current_billing_period_start_date) {
    t4 = () => HookdeckAPI.billing.getUsage({
      timeframe_start: subscription_details?.current_billing_period_start_date,
      timeframe_end: subscription_details?.current_billing_period_end_date
    });
    $[10] = HookdeckAPI.billing;
    $[11] = subscription_details?.current_billing_period_end_date;
    $[12] = subscription_details?.current_billing_period_start_date;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const {
    data: usage_data
  } = useSWR(t3, t4);
  let used_events = 0;
  let used_requests = 0;
  if ($[14] !== usage_data?.data) {
    for (const entry of usage_data?.data || []) {
      const name = entry.billable_metric.name.toLowerCase();
      if (name === "events") {
        used_events = entry.usage[0]?.quantity || 0;
      } else {
        if (name === "discarded requests") {
          used_requests = entry.usage[0]?.quantity || 0;
        }
      }
    }
    $[14] = usage_data?.data;
    $[15] = used_events;
    $[16] = used_requests;
  } else {
    used_events = $[15];
    used_requests = $[16];
  }
  let included_events = 0;
  let included_requests = 0;
  if ($[17] !== subscription_details?.plan?.prices) {
    for (const price of subscription_details?.plan?.prices || []) {
      const name_0 = price.name.toLowerCase();
      if (name_0 === "events") {
        included_events = Number(price.package_with_allocation_config?.allocation) || 0;
      } else {
        if (name_0 === "discarded requests") {
          included_requests = Number(price.package_with_allocation_config?.allocation) || 0;
        }
      }
    }
    $[17] = subscription_details?.plan?.prices;
    $[18] = included_events;
    $[19] = included_requests;
  } else {
    included_events = $[18];
    included_requests = $[19];
  }
  included_events = included_events || 10000;
  const is_high_usage = used_events > included_events * 0.7;
  const t5 = is_free_plan ? !subscription_details || !usage_data : false;
  let t6;
  if ($[20] !== included_events || $[21] !== included_requests || $[22] !== is_high_usage || $[23] !== t5 || $[24] !== used_events || $[25] !== used_requests) {
    t6 = {
      loading: t5,
      used_events,
      included_events,
      used_requests,
      included_requests,
      is_high_usage
    };
    $[20] = included_events;
    $[21] = included_requests;
    $[22] = is_high_usage;
    $[23] = t5;
    $[24] = used_events;
    $[25] = used_requests;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  return t6;
};
const OrganizationUsageCard = () => {
  const $ = _c(50);
  const {
    used_events,
    included_events,
    used_requests,
    included_requests
  } = useOrganizationUsage();
  const show_discarded = included_requests > 0 && used_requests >= included_requests && used_events < included_events;
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let config;
  let t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== included_events || $[1] !== included_requests || $[2] !== show_discarded || $[3] !== used_events || $[4] !== used_requests) {
    const discarded_requests_config = {
      title: "Discarded requests",
      metric: "discarded requests",
      included: included_requests,
      used: used_requests
    };
    const events_config = {
      title: "Included events",
      metric: "events",
      included: included_events,
      used: used_events
    };
    config = show_discarded ? discarded_requests_config : events_config;
    T4 = CardsWrapper;
    T3 = StyledMemberTrialCard;
    T2 = StyledCardSection;
    t3 = 3;
    T1 = Div;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        align: "center",
        justify: "flex-start"
      };
      $[15] = t1;
    } else {
      t1 = $[15];
    }
    t2 = <Text semi={true}>{config.title}</Text>;
    T0 = Tooltip;
    t0 = config.included.toLocaleString();
    $[0] = included_events;
    $[1] = included_requests;
    $[2] = show_discarded;
    $[3] = used_events;
    $[4] = used_requests;
    $[5] = T0;
    $[6] = T1;
    $[7] = T2;
    $[8] = T3;
    $[9] = T4;
    $[10] = config;
    $[11] = t0;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
  } else {
    T0 = $[5];
    T1 = $[6];
    T2 = $[7];
    T3 = $[8];
    T4 = $[9];
    config = $[10];
    t0 = $[11];
    t1 = $[12];
    t2 = $[13];
    t3 = $[14];
  }
  const t4 = `The Developer (free) plan includes up to ${t0} ${config.metric}, distributed across all projects on this organization. Upgrade to increase usage limits. `;
  let t5;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      label: "Billing",
      to: "/settings/organization/billing",
      icon: "wallet"
    };
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Icon icon="info" muted={true} m={{
      l: 1
    }} />;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== T0 || $[19] !== t4) {
    t7 = <T0 tooltip={t4} cta={t5}>{t6}</T0>;
    $[18] = T0;
    $[19] = t4;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== T1 || $[22] !== t1 || $[23] !== t2 || $[24] !== t7) {
    t8 = <T1 flex={t1}>{t2}{t7}</T1>;
    $[21] = T1;
    $[22] = t1;
    $[23] = t2;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      y: 2
    };
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const t10 = config.used / config.included;
  const t11 = config.used >= config.included;
  let t12;
  if ($[27] !== t10 || $[28] !== t11) {
    t12 = <ProgressBar m={t9} value={t10} danger={t11} />;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const t13 = config.used < config.included ? config.used : config.included;
  let t14;
  if ($[30] !== t13) {
    t14 = <NumberFormat renderText={_temp4} displayType="text" value={t13} thousandSeparator="," decimalScale={0} />;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== config.included) {
    t15 = <NumberFormat renderText={_temp5} displayType="text" value={config.included} thousandSeparator="," />;
    $[32] = config.included;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t14 || $[35] !== t15) {
    t16 = <Text muted={true}>{t14}/{t15}{" "}used</Text>;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Button to={_temp6} block={true} outline={true} data-action="upgrade-plan-usage-navbar-card" m={{
      t: 3
    }}>Upgrade plan</Button>;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== T2 || $[39] !== t12 || $[40] !== t16 || $[41] !== t3 || $[42] !== t8) {
    t18 = <T2 p={t3}>{t8}{t12}{t16}{t17}</T2>;
    $[38] = T2;
    $[39] = t12;
    $[40] = t16;
    $[41] = t3;
    $[42] = t8;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== T3 || $[45] !== t18) {
    t19 = <T3>{t18}</T3>;
    $[44] = T3;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== T4 || $[48] !== t19) {
    t20 = <T4>{t19}</T4>;
    $[47] = T4;
    $[48] = t19;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  return t20;
};
const NavFooter = () => {
  const $ = _c(91);
  const {
    user,
    subscription,
    organization,
    team,
    show_get_started,
    setShowGetStarted,
    organization_role,
    nav_collapsed,
    setNavCollapsed
  } = useContext(DashboardContext);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    is_member_trial_active,
    is_member_trial_expired
  } = useMemberTrial(subscription);
  const {
    is_high_usage,
    loading: is_usage_loading
  } = useOrganizationUsage();
  const [hide_member_trial_modal, setHideMemberTrialModal] = useLocalStorage(`${storage_keys.hide_member_trial_modal}:${user?.id}:${user?.last_team_id}`, false);
  let t0;
  if ($[0] !== organization.id) {
    t0 = APIMethodKeys.organizations.listMembers(organization.id);
    $[0] = organization.id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== HookdeckAPI.organizations) {
    t1 = () => HookdeckAPI.organizations.listMembers();
    $[2] = HookdeckAPI.organizations;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data: members
  } = useSWR(t0, t1);
  const exceeds_users = subscription.max_users && members && members.length >= subscription.max_users;
  const invite_to_trial = !!exceeds_users && !organization?.sso_enabled;
  const theme = useTheme();
  const history = useHistory();
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      product: "event_gateway"
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    showFeed,
    unread_count,
    ChangelogCard,
    ChangelogFeed
  } = useChangelog(t2);
  let t3;
  if ($[5] !== hide_member_trial_modal || $[6] !== invite_to_trial || $[7] !== is_member_trial_active || $[8] !== is_member_trial_expired || $[9] !== organization_role) {
    t3 = !hide_member_trial_modal && (is_member_trial_expired && ["admin", "owner"].includes(organization_role) || is_member_trial_active && ["admin", "owner"].includes(organization_role) || is_member_trial_active && !["admin", "owner"].includes(organization_role) || invite_to_trial && ["admin", "owner"].includes(organization_role));
    $[5] = hide_member_trial_modal;
    $[6] = invite_to_trial;
    $[7] = is_member_trial_active;
    $[8] = is_member_trial_expired;
    $[9] = organization_role;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const is_member_trial_rendering = t3;
  let t4;
  if ($[11] !== subscription.plan) {
    t4 = !!subscription?.plan && isFreePlan(subscription.plan);
    $[11] = subscription.plan;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const is_free_plan = t4;
  const is_usage_rendering = is_free_plan;
  const {
    refresh_key
  } = useRefreshKey();
  let t10;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[13] !== refresh_key) {
    const metrics_now = new Date(refresh_key);
    t7 = useMetric;
    t8 = "chart:line";
    t9 = "events_max_rate";
    t10 = refresh_key;
    t5 = subHours(metrics_now, 6).toISOString();
    t6 = metrics_now.toISOString();
    $[13] = refresh_key;
    $[14] = t10;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t10 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t11;
  if ($[20] !== t5 || $[21] !== t6) {
    t11 = {
      date_range: {
        min: t5,
        max: t6
      }
    };
    $[20] = t5;
    $[21] = t6;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const max_events = t7(t8, t9, t10, t11);
  let throughput_status;
  if ($[23] !== max_events.data || $[24] !== team) {
    throughput_status = null;
    if (max_events.data && max_events.data.data) {
      const max_event = Math.max(...max_events.data.data.map(_temp7));
      if (team.max_events_per_second < (max_events.data.data[max_events.data.data.length - 1].y as number)) {
        throughput_status = "danger";
      } else {
        if (max_event > team.max_events_per_second) {
          throughput_status = "warn";
        } else {
          throughput_status = "ok";
        }
      }
    }
    $[23] = max_events.data;
    $[24] = team;
    $[25] = throughput_status;
  } else {
    throughput_status = $[25];
  }
  const has_team_trial_card = is_member_trial_rendering && !is_high_usage && !subscription?.limit_exceeded_at;
  const has_changelog_card = !!ChangelogCard;
  const has_usage_card = is_usage_rendering;
  const has_cards = is_usage_loading ? false : has_team_trial_card || has_changelog_card || has_usage_card;
  const show_throughput_indicator = throughput_status === "warn" || throughput_status === "danger";
  let t12;
  if ($[26] !== ChangelogCard || $[27] !== has_cards || $[28] !== has_changelog_card || $[29] !== has_team_trial_card || $[30] !== has_usage_card || $[31] !== is_usage_rendering || $[32] !== nav_collapsed || $[33] !== setHideMemberTrialModal) {
    t12 = has_cards && !nav_collapsed && <CardsWrapper>{Number(has_team_trial_card) + Number(has_changelog_card) + Number(has_usage_card) >= 2 && <CardBackgroundHint />}{has_team_trial_card ? <MemberTrialCard dismissCard={() => setHideMemberTrialModal(true)} /> : ChangelogCard ? ChangelogCard : is_usage_rendering && <OrganizationUsageCard />}</CardsWrapper>;
    $[26] = ChangelogCard;
    $[27] = has_cards;
    $[28] = has_changelog_card;
    $[29] = has_team_trial_card;
    $[30] = has_usage_card;
    $[31] = is_usage_rendering;
    $[32] = nav_collapsed;
    $[33] = setHideMemberTrialModal;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== HookdeckAPI.track || $[36] !== throughput_status) {
    t13 = opened => {
      if (opened) {
        HookdeckAPI.track.event("Clicked on Project Throughput", {
          throughput_status
        });
      }
    };
    $[35] = HookdeckAPI.track;
    $[36] = throughput_status;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== nav_collapsed || $[39] !== show_throughput_indicator || $[40] !== throughput_status) {
    t14 = (opened_0, toggle) => <StyledNavLinks><ClickableArea highlight={opened_0} on="background" w={nav_collapsed ? undefined : 100} flex={{
        align: "center",
        justify: nav_collapsed ? "center" : undefined
      }} onClick={() => toggle(!opened_0)}>{nav_collapsed ? <Tooltip tooltip="Project Throughput" side="right" align="center" offset={[0, 8]}><CollapsedNavIcon icon="throughput" dot_color={show_throughput_indicator ? "warning" : undefined} /></Tooltip> : <Icon left={true} icon="throughput" pointer={true} p={0.5} />}{!nav_collapsed && <><Text semi={true} size="m" w={100} flex={{
            justify: "space-between",
            align: "center"
          }}>Project Throughput</Text><div>{throughput_status === "warn" && <Icon m={{
              r: 0.5
            }} icon="warning" warning={true} pointer={true} />}{throughput_status === "danger" && <Icon m={{
              r: 0.5
            }} icon="error" danger={true} pointer={true} />}</div></>}</ClickableArea></StyledNavLinks>;
    $[38] = nav_collapsed;
    $[39] = show_throughput_indicator;
    $[40] = throughput_status;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== max_events || $[43] !== team || $[44] !== throughput_status) {
    t15 = toggle_0 => <><StyledCardSection w={{
        px: 600
      }}>{max_events.error ? <Div h={{
          px: 168
        }} flex={{
          direction: "column",
          align: "center",
          justify: "center"
        }} p={3}><Icon icon="error" danger={true} m={{
            b: 1
          }} /><Text semi={true} center={true}>Unable to load throughput</Text><Text muted={true} center={true} size="s">Something went wrong loading your events. Please try again.</Text></Div> : <Div m={{
          t: 8
        }}><LineChart compact={true} height={168} loading={!max_events.data} datasets={max_events && max_events.data ? [{
            key: "delivered",
            label: "Max new events per second",
            theme_color: "primary" as const,
            mono: false,
            data: max_events.data.data,
            total: max_events.data.total,
            highest: max_events.data.highest,
            average: max_events.data.average,
            getDataLabel: _temp8,
            metric_type: "max" as const
          }, {
            key: "limit",
            label: "Throughput limit",
            theme_color: "danger" as const,
            mono: false,
            dashed: true,
            data: max_events.data.data.map(d_0 => ({
              ...d_0,
              y: team.max_events_per_second
            })),
            total: team.max_events_per_second * max_events.data.data.length,
            highest: team.max_events_per_second,
            average: team.max_events_per_second,
            getDataLabel: _temp9,
            metric_type: "rate" as const
          }] : []} /></Div>}</StyledCardSection><StyledCardSection p={{
        x: 4,
        y: 3
      }} flex={{
        justify: "space-between",
        align: "center",
        gap: 2
      }}><Text muted={true} flex={{
          grow: true
        }} min_w={{
          px: 0
        }}>{throughput_status === "ok" && "Events within project throughput limit."}{throughput_status === "warn" && "Events exceeded project throughput limit within past 6 hours"}{throughput_status === "danger" && "Events are exceeding project throughput limit"}</Text><Button outline={true} icon="link" to="/settings/project/quotas" style={{
          flexShrink: 0
        }} onClick={() => toggle_0(false)}>View throughput</Button></StyledCardSection></>;
    $[42] = max_events;
    $[43] = team;
    $[44] = throughput_status;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== t13 || $[47] !== t14 || $[48] !== t15) {
    t16 = <Dropdown placement="right-end" block={true} onToggle={t13} renderToggle={t14}>{t15}</Dropdown>;
    $[46] = t13;
    $[47] = t14;
    $[48] = t15;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  let t18;
  if ($[50] !== nav_collapsed) {
    t17 = nav_collapsed ? <Tooltip tooltip="Settings" side="right" align="center" offset={[0, 8]}><Icon icon="settings" pointer={true} p={0.5} /></Tooltip> : <Icon left={true} icon="settings" pointer={true} p={0.5} />;
    t18 = !nav_collapsed && <Text semi={true} size="m">Settings</Text>;
    $[50] = nav_collapsed;
    $[51] = t17;
    $[52] = t18;
  } else {
    t17 = $[51];
    t18 = $[52];
  }
  let t19;
  if ($[53] !== t17 || $[54] !== t18) {
    t19 = <NavLink to="/settings">{t17}{t18}</NavLink>;
    $[53] = t17;
    $[54] = t18;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== nav_collapsed || $[57] !== unread_count) {
    t20 = (opened_1, toggle_1) => <StyledNavLinks><ClickableArea w={nav_collapsed ? undefined : 100} flex={{
        align: "center",
        justify: nav_collapsed ? "center" : undefined
      }} highlight={opened_1} onClick={() => toggle_1(!opened_1)}>{nav_collapsed ? <Tooltip tooltip="Help" side="right" align="center" offset={[0, 8]}><Icon icon="help" pointer={true} p={0.5} /></Tooltip> : <Icon left={true} icon="help" pointer={true} p={0.5} />}{!nav_collapsed && <><Text semi={true} size="m">Help</Text>{unread_count > 0 && <Badge primary={true} subtle={true} m={{
            l: "auto"
          }}>{unread_count}</Badge>}</>}</ClickableArea></StyledNavLinks>;
    $[56] = nav_collapsed;
    $[57] = unread_count;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== history || $[60] !== setShowGetStarted || $[61] !== showFeed || $[62] !== show_get_started || $[63] !== unread_count) {
    t21 = toggle_2 => <><StyledCardSection p={1} flex={{
        direction: "column"
      }}>{!show_get_started && <ClickableArea p={{
          x: 2,
          y: 1.5
        }} flex={{
          align: "center"
        }} rounded={true} onClick={() => {
          history.push("/get-started");
          setShowGetStarted(true);
          toggle_2(false);
        }}><Icon pointer={true} icon="grid_view" left={true} muted={true} p={0.5} /><Text semi={true} size="m">Get Started</Text></ClickableArea>}<ClickableArea p={{
          x: 2,
          y: 1.5
        }} as="a" href={LINKS.docs} target="_blank" rel="noreferrer" flex={{
          align: "center"
        }} rounded={true}><Icon pointer={true} icon="docs" left={true} muted={true} p={0.5} /><Text semi={true} size="m">Documentation</Text></ClickableArea><ClickableArea p={{
          x: 2,
          y: 1.5
        }} as="a" href={LINKS.api_ref.root} target="_blank" rel="noreferrer" flex={{
          align: "center"
        }} rounded={true}><Icon pointer={true} icon="api" left={true} muted={true} p={0.5} /><Text semi={true} size="m">API Reference</Text></ClickableArea><ClickableArea flex={{
          align: "center"
        }} rounded={true} p={{
          x: 2,
          y: 1.5
        }} onClick={() => {
          showFeed();
          toggle_2(false);
        }}><Icon pointer={true} icon="new_releases" left={true} muted={true} p={0.5} /><Text semi={true} size="m" w={100} flex={{
            justify: "space-between",
            align: "center"
          }}>Changelog{unread_count > 0 && <Badge primary={true} subtle={true}>{unread_count}</Badge>}</Text></ClickableArea></StyledCardSection><StyledCardSection p={1} min_w={{
        px: 180
      }} flex={{
        direction: "column"
      }}><ClickableArea p={{
          x: 2,
          y: 1.5
        }} flex={{
          align: "center"
        }} rounded={true} onClick={showChat}><Icon pointer={true} left={true} icon="chat" muted={true} p={0.5} /><Text semi={true} size="m">Live Chat</Text></ClickableArea><ClickableArea p={{
          x: 2,
          y: 1.5
        }} flex={{
          align: "center"
        }} rounded={true} as="a" href="mailto:info@hookdeck.com" target="_blank" rel="noreferrer"><Icon pointer={true} icon="email" left={true} muted={true} p={0.5} /><Text semi={true} size="m">Email us</Text></ClickableArea><ClickableArea p={{
          x: 2,
          y: 1.5
        }} flex={{
          align: "center"
        }} rounded={true} as="a" href={LINKS.community} target="_blank" rel="noreferrer"><Icon pointer={true} icon="slack_colored" left={true} p={0.5} /><Text semi={true} size="m">Join Slack</Text></ClickableArea></StyledCardSection></>;
    $[59] = history;
    $[60] = setShowGetStarted;
    $[61] = showFeed;
    $[62] = show_get_started;
    $[63] = unread_count;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  let t22;
  if ($[65] !== t20 || $[66] !== t21) {
    t22 = <Dropdown placement="right-end" block={true} p={0} renderToggle={t20}>{t21}</Dropdown>;
    $[65] = t20;
    $[66] = t21;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  let t23;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      min: 225
    };
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] !== nav_collapsed || $[70] !== setNavCollapsed || $[71] !== user.name) {
    t24 = (opened_2, toggle_3) => <StyledNavLinks style={{
      flexDirection: nav_collapsed ? "column" : "row",
      alignItems: nav_collapsed ? "center" : "stretch"
    }}><ClickableArea highlight={opened_2} on="background" w={nav_collapsed ? {
        px: 28
      } : undefined} style={{
        flex: nav_collapsed ? "0 0 auto" : "1 1 auto",
        minWidth: 0
      }} flex={{
        align: "center",
        justify: nav_collapsed ? "center" : "space-between"
      }} onClick={() => toggle_3(!opened_2)}><Div w={nav_collapsed ? undefined : {
          px: 182
        }} flex={{
          align: "center",
          justify: nav_collapsed ? "center" : undefined
        }}>{nav_collapsed ? <Tooltip tooltip={capitalizeFirstLetter(user.name)} side="right" align="center" offset={[0, 8]}><Avatar name={user.name} small={true} neutral={true} /></Tooltip> : <Avatar name={user.name} small={true} neutral={true} m={{
            r: 2
          }} />}{!nav_collapsed && <Text semi={true} ellipsis={true}>{capitalizeFirstLetter(user.name)}</Text>}</Div></ClickableArea>{nav_collapsed ? <Tooltip tooltip="Expand sidebar" side="right" align="center" offset={[0, 8]}><ClickableArea aria-label="Expand sidebar" on="background" rounded={true} w={{
          px: 28
        }} h={{
          px: 28
        }} flex={{
          align: "center",
          justify: "center"
        }} p={1} onClick={() => setNavCollapsed(false)}><Icon muted={true} icon="left_panel_open" pointer={true} p={0.5} /></ClickableArea></Tooltip> : <Tooltip tooltip="Collapse sidebar" side="right" align="center" offset={[0, 8]}><ClickableArea aria-label="Collapse sidebar" on="background" rounded={true} w={{
          px: 28
        }} h={{
          px: 28
        }} flex={{
          align: "center",
          justify: "center"
        }} p={1} onClick={() => setNavCollapsed(true)}><Icon muted={true} icon="left_panel_close" pointer={true} p={0.5} /></ClickableArea></Tooltip>}</StyledNavLinks>;
    $[69] = nav_collapsed;
    $[70] = setNavCollapsed;
    $[71] = user.name;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  let t25;
  if ($[73] !== HookdeckAPI || $[74] !== theme || $[75] !== user.email || $[76] !== user.name) {
    t25 = toggle_4 => <><Div p={{
        x: 4,
        y: 3
      }}><Text ellipsis={true} semi={true}>{user.name}</Text><Text ellipsis={true} semi={true} muted={true}>{user.email}</Text></Div><Divider /><Div p={1}><ClickableArea to="/settings/account/profile" onClick={() => toggle_4(false)} p={{
          x: 2,
          y: 1.5
        }} rounded={true} flex={{
          align: "center"
        }}><Icon icon="settings" p={0.5} left={true} pointer={true} muted={true} /><Text semi={true}>Account Settings</Text></ClickableArea><Dropdown placement="right-start" portal={true} block={true} p={0} parent_width={{
          min: 225
        }} renderToggle={(opened_3, toggle_5) => <ClickableArea rounded={true} highlight={opened_3} flex={{
          align: "center",
          justify: "space-between"
        }} p={{
          x: 2,
          y: 1.5
        }} onClick={() => toggle_5(!opened_3)}><Div flex={{
            align: "center"
          }}><Icon p={0.5} pointer={true} muted={true} icon={theme.selected === "system" ? "computer" : theme.selected === "light" ? "light_mode" : "dark_mode"} left={true} /><Text semi={true} dark={true}>Theme</Text></Div><Icon icon="chevron_right" left={1} pointer={true} /></ClickableArea>}>{toggle_6 => <Div p={1}><ClickableArea p={{
              x: 2,
              y: 1.5
            }} flex={{
              align: "center",
              justify: "space-between"
            }} rounded={true} onClick={() => {
              toggle_6(false);
              theme.toggleTheme("light");
            }}><Div flex={{
                align: "center"
              }}><Icon icon="light_mode" left={true} muted={true} pointer={true} p={0.5} /><Text semi={true} size="m">Light</Text></Div>{theme.selected === "light" && <Icon icon="success" pointer={true} primary={true} />}</ClickableArea><ClickableArea p={{
              x: 2,
              y: 1.5
            }} flex={{
              align: "center",
              justify: "space-between"
            }} rounded={true} onClick={() => {
              toggle_6(false);
              theme.toggleTheme("dark");
            }}><Div flex={{
                align: "center"
              }}><Icon icon="dark_mode" left={true} muted={true} pointer={true} p={0.5} /><Text semi={true} size="m">Dark</Text></Div>{theme.selected === "dark" && <Icon icon="success" pointer={true} primary={true} />}</ClickableArea><ClickableArea p={{
              x: 2,
              y: 1.5
            }} flex={{
              align: "center",
              justify: "space-between"
            }} rounded={true} onClick={() => {
              toggle_6(false);
              theme.toggleTheme("system");
            }}><Div flex={{
                align: "center"
              }}><Icon icon="computer" left={true} muted={true} pointer={true} p={0.5} /><Text semi={true} size="m">System</Text></Div>{theme.selected === "system" && <Icon icon="success" pointer={true} primary={true} />}</ClickableArea></Div>}</Dropdown></Div><Divider /><Div p={1}><ClickableArea onClick={() => {
          toggle_4(false);
          handleLogout(HookdeckAPI);
        }} p={{
          x: 2,
          y: 1.5
        }} rounded={true} flex={{
          align: "center"
        }}><Icon icon="logout" left={2} p={0.5} pointer={true} muted={true} /><Text semi={true}>Sign out</Text></ClickableArea></Div></>;
    $[73] = HookdeckAPI;
    $[74] = theme;
    $[75] = user.email;
    $[76] = user.name;
    $[77] = t25;
  } else {
    t25 = $[77];
  }
  let t26;
  if ($[78] !== t24 || $[79] !== t25) {
    t26 = <Dropdown placement="right-end" parent_width={t23} renderToggle={t24}>{t25}</Dropdown>;
    $[78] = t24;
    $[79] = t25;
    $[80] = t26;
  } else {
    t26 = $[80];
  }
  let t27;
  if ($[81] !== t16 || $[82] !== t19 || $[83] !== t22 || $[84] !== t26) {
    t27 = <StyledNavLinks>{t16}{t19}{t22}{t26}</StyledNavLinks>;
    $[81] = t16;
    $[82] = t19;
    $[83] = t22;
    $[84] = t26;
    $[85] = t27;
  } else {
    t27 = $[85];
  }
  let t28;
  if ($[86] !== ChangelogFeed || $[87] !== nav_collapsed || $[88] !== t12 || $[89] !== t27) {
    t28 = <StyledNavFooter collapsed={nav_collapsed}>{t12}{t27}{ChangelogFeed}</StyledNavFooter>;
    $[86] = ChangelogFeed;
    $[87] = nav_collapsed;
    $[88] = t12;
    $[89] = t27;
    $[90] = t28;
  } else {
    t28 = $[90];
  }
  return t28;
};
export default NavFooter;
function _temp(location) {
  return {
    ...location,
    pathname: "/settings/organization/plans",
    state: {
      scroll: false
    }
  };
}
function _temp2(location_0) {
  return {
    ...location_0,
    pathname: "/settings/organization/members/add",
    state: {
      scroll: false
    }
  };
}
function _temp3(location_1) {
  return {
    ...location_1,
    pathname: "/settings/organization/members/add",
    state: {
      scroll: false
    }
  };
}
function _temp4(v) {
  return v;
}
function _temp5(v_0) {
  return v_0;
}
function _temp6(location) {
  return {
    ...location,
    pathname: "/settings/organization/plans",
    state: {
      scroll: false
    }
  };
}
function _temp7(d) {
  return d.y as number;
}
function _temp8(v) {
  return `${formatChartRateNumber(v, "second")} / second`;
}
function _temp9(v_0) {
  return `${formatChartRateNumber(v_0, "second")} / second`;
}